import React from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Link,
  Paper,
  Typography as MuiTypography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { ExpandMore } from "@material-ui/icons";

import { NavLink } from "react-router-dom";

import BasicReviewRegistrationInfo from "./BasicReviewRegistrationInfo";

import { copyToClipboard, dateFormatter } from "../../utils";
import { useApp } from "../../AppProvider";
import { useReviewRegistrations } from "./useReviewRegistrations";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;
function ReviewRegistrationsManagement() {
  const {
    selectedRegistration,
    setSelectedRegistration,
    data,
    isFetching,
    error,
    refetch,
    REVIEW_REGISTRATIONS_TABLE_COLUMNS,
  } = useReviewRegistrations();
  const { doToast } = useApp();
  const { getAccessTokenSilently } = useAuth0();

  const [triggerRefetch, setTriggerRefetch] = React.useState(false);
  const handleTriggerRefetch = () => setTriggerRefetch(!triggerRefetch);
  const handleRefetch = () => {
    refetch().then(() => {
      handleTriggerRefetch();
    });
  };

  const handleDelete = (oldData) => {
    return (async () => {
      try {
        if (oldData) {
          const token = await getAccessTokenSilently();
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(
            `${process.env.REACT_APP_ENDPOINT}/api/data-registrations/${oldData.reg_ndx}`,
            { headers }
          );
          doToast("success", "This entry was deleted from the database");
          if (selectedRegistration === oldData.reg_ndx) {
            selectedRegistration(null);
          }

          await refetch();
        } else {
          doToast("error", "Something went wrong");
        }
      } catch (err) {
        console.error(err);
        const message = err?.message ?? "Something went wrong";
        doToast("error", message);
      }
    })();
  };

  if (error) return "An error has occurred: " + error.message;
  return (
    <React.Fragment>
      <Helmet title="Public Well Registrations" />
      <Typography variant="h3" gutterBottom display="inline">
        Review Public Well Registrations
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Public Well Registrations</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">
                Public Registration Selection
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableWrapper>
              <MaterialTable
                id="Review Public Registrations"
                title={`Review Public Registrations ${dateFormatter(
                  new Date(),
                  "MM/DD/YYYY, h:mm A"
                )}`}
                loading={isFetching || !data}
                columns={REVIEW_REGISTRATIONS_TABLE_COLUMNS}
                data={data}
                localization={{
                  toolbar: {
                    searchPlaceholder: "Public Registrations Selection",
                  },
                }}
                components={{
                  Container: (props) => <div {...props} />,
                }}
                editable={{
                  onRowDelete: handleDelete,
                }}
                actions={[
                  {
                    icon: CopyIcon,
                    tooltip: "Copy Data",
                    isFreeAction: true,
                    onClick: () => {
                      try {
                        copyToClipboard(
                          data,
                          REVIEW_REGISTRATIONS_TABLE_COLUMNS,
                          () =>
                            doToast(
                              "success",
                              "Data was copied to your clipboard."
                            )
                        );
                      } catch (error) {
                        const message =
                          error?.message ?? "Something went wrong";
                        doToast("error", message);
                      }
                    },
                  },
                  () => ({
                    icon: "search",
                    tooltip: "Review",
                    onClick: (event, rowData) => {
                      setSelectedRegistration(rowData?.reg_ndx);
                    },
                  }),
                ]}
                options={{
                  filtering: true,
                  emptyRowsWhenPaging: false,
                  showTitle: false,
                  columnsButton: true,
                  exportButton: true,
                  exportAllData: true,
                  addRowPosition: "first",
                  pageSize: 30,
                  pageSizeOptions: [10, 30, 50, 100, 200],
                  padding: "dense",
                  searchFieldAlignment: "left",
                  minBodyHeight: "500px",
                  maxBodyHeight: "calc(100vh - 550px)",
                }}
              />
            </TableWrapper>
          </AccordionDetails>
        </Accordion>

        <Box
          style={{
            display:
              selectedRegistration || selectedRegistration === 0
                ? "block"
                : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                Reviewing Public Registration
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedRegistration(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
          <BasicReviewRegistrationInfo
            selectedRegistration={selectedRegistration}
            setSelectedRegistration={setSelectedRegistration}
            refetchRegistrations={handleRefetch}
            triggerRefetch={triggerRefetch}
          />
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                Reviewing Public Registration
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedRegistration(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default ReviewRegistrationsManagement;
